import React, { useEffect, useRef, useState } from 'react';
import { go } from './nav.js';
import { call, getToken, saveMe } from './api.js';
import { RAILS, railQuery, countLabel } from './rails.js';
/* ⚠️ THE SEARCH BAR IS THE ONE `Zaryna.jsx` DRAWS TOO — `SearchBar` in
   ui.jsx. Al, 24 September 2026: *"the search bar on the home screen should
   also be the same as the google search bar and mic."*  Two copies of it would
   drift exactly the way two copies of the venue card did. */
/* ⚠️ THE PIN IS NO LONGER INSIDE THAT BAR. Al, 24 September 2026: *"the
   location, search bar and mic look too cramped you can put location on the
   top bar and expand the search bar and mic."*  `LocationPin` is the same
   control, drawn in `.htop` instead. */
import { VenueCard, BrandCard, TabBar, SearchBar, LocationPin, useSpeech, onNowFirst, MicStatus } from './ui.jsx';
import { useZarynaAnswer, ZarynaAnswer, keptQuestion } from './zask.jsx';
import { Carousel } from './Carousel.jsx';
import * as rotate from './rotate.js';
/* ⚠️ THE ONE MODULE THAT KNOWS WHERE A BRAND CARD GOES. Home receives the
   three numbers from the server and hands them straight to it — none of them
   is written down here, and `10` never appears in this file. */
import { slotsFor } from './placement.js';
import { shown } from './track.js';
/* ⚠️ THE ONE MODULE THAT KNOWS WHERE SOMEBODY IS. Home used to read
   localStorage itself, which made it the second thing that knew the rule. */
/* ⚠️ AND THE ONE MODULE THAT KNOWS WHICH MARKET TO ASK FOR. `MARKET =
   'hong-kong'` used to be typed into this file — see `marketFor` in place.js
   for what that cost somebody standing in Calgary. */
import { useCentre, whereCity, radiusLadder, marketFor, browseMarket,
  centreFrom, writeCentre, browsingName, readCentre, metresBetween, WELCOME_KEY } from './place.js';
import { rememberLanguage, adoptAccountLanguage, screenLanguage, rememberCityLanguages, rememberBrowsingCity, cityLanguages } from './voice.js';
import { uiLang, zh, hotKeyLabel, languageMoved } from './i18n.js';
const normLang = (c) => (/^zh-(hk|mo|tw|hant)/i.test(c || '') ? 'zh-HK' : /^zh/i.test(c || '') ? 'zh-CN' : /^fr/i.test(c || '') ? 'fr-CA' : /^gu/i.test(c || '') ? 'gu-IN' : 'en');

/* ══════════════════════════════════════════════════════════════════════════
 * HOME — the screen an Eleater lands on
 *
 * Built from `10_Home_LOCKED.html`: the hero line, the four hot keys, the
 * four rails in their locked order with "View All (n)" beside each, and the
 * tab bar. Every heading is quoted from that file — RULE 4, copy is never
 * invented.
 *
 * ── WHAT THIS REPLACES ──────────────────────────────────────────────────────
 *
 * The temporary list in main.jsx, which called itself "a door held open until
 * Home and Explore are built" and said so on screen.
 *
 * ⚠️ AND IT ASKS THE SERVER, NOT A FILE. The locked screen reads
 * `eleat_index.js`, a file built on a laptop. That works on a desk and cannot
 * ship: the real app has to ask, and until 22 September nothing on the server
 * could answer "which venues have a Happy Hour". The deal_type filter added
 * that, and this is the first screen to use it.
 *
 * ── A RAIL WITH NOTHING IN IT IS NOT DRAWN ──────────────────────────────────
 *
 * The same rule as the venue page's tabs, one level up. Al, 22 September:
 * *"if they don't put anything in, for example, Game Day, then Game Day
 * should not show up."*  On a venue that is a tab; here it is a whole row.
 * A market where nobody does Game Day shows three rails, not four with an
 * empty one — and on the real data this morning Game Day had 5 venues where
 * the others had 50, so this is the ordinary case, not the edge.
 * ══════════════════════════════════════════════════════════════════════════ */

/* ⚠️⚠️ `const MARKET = 'hong-kong'` STOOD HERE AND IT IS GONE. Al, 25
 * September 2026: *"stop sending MARKET = 'hong-kong'. Use the active
 * discovery centre's market where a market is required."*  `marketFor()` owns
 * the three answers now — the centre's market, the launch market when nothing
 * has been chosen or found at all, and **null for a real place Eleat is not
 * in**, which is drawn as such below rather than quietly served Hong Kong. */

/* ══════════════════════════════════════════════════════════════════════════
 * THE HOT KEYS — `10_Home_LOCKED.html`, THE `POOL` ARRAY, WORD FOR WORD
 *
 * ⚠️ NINE, AND TWO AT A TIME. The locked screen holds nine and shows two,
 * swapping the pair every seven seconds with a fade. Not four, and not the
 * four offer names — those are the PILLARS, a different control that sits
 * below this one.
 *
 * ⚠️⚠️⚠️ AND THEY WERE NEVER SEARCHES — EVERY ONE OF THEM LANDED ON AN EMPTY
 * SCREEN. 30 September 2026, proved against the live server before a word of
 * diagnosis:
 *
 *   q=Happy hour in LKF     → resolved_as "name"     → 0 results
 *   q=Lan Kwai Fong         → resolved_as "location" → 6 results
 *
 * **The search was never broken.** `q` resolves a restaurant NAME, an AREA or
 * a CUISINE — never a sentence. So this array was handed to the resolver as
 * though each phrase were a venue's name, found nothing, and dropped the
 * Eleater on a blank Explore. Nine chips, nine dead ends, and nothing threw:
 * an empty result set renders exactly as well as a full one.
 *
 * ⚠️ AND THE ARRAY ITSELF WAS THE OTHER HALF OF THE FAULT. Al, 30 September:
 * *"I don't know what the hell you're talking about, that these are hard-coded
 * in. They shouldn't be. I should be able to change them in the Eleat Super
 * Admin."*  They are rows now — `home_hot_keys`, migration 105 — carrying the
 * WORDS and the SEARCH as two separate things, which is the only way the
 * phrase stops being mistaken for a venue's name.
 *
 * ⚠️ NOTHING IS DRAWN IF THE ROUTE SAYS NOTHING. No hard-coded fallback list,
 * deliberately: a fallback here would put the nine dead chips back on the
 * screen on exactly the days the server is having trouble, and they would look
 * identical to working ones. An empty row is one gap in the layout.
 * ══════════════════════════════════════════════════════════════════════════ */
const KEYS_SHOWN = 2;
const KEYS_TURN_MS = 7000;

/* ══════════════════════════════════════════════════════════════════════════
 * THE HEADLINE — `10_Home_LOCKED.html`, THE `H` ARRAY, ALL NINE
 *
 * React drew ONE of them and never moved. Al, 9 September 2026, about exactly
 * that: *"why does last minute special stick there. we had many others that
 * should scroll."*  He said it about the prototype; the prototype was fixed
 * that day and the React build was written afterwards with the fault back in.
 *
 * ⚠️ "Central" IS SAMPLE CONTENT — RULE ZERO-B. The locked array reads
 * "…in Central" five times because the file it lives in is a screen with
 * sample data on it. The pin is wherever the Eleater is, so the area is a
 * SLOT here, never a word.
 *
 * ⚠️ AND WHEN ELEAT DOES NOT KNOW THE AREA, THOSE FIVE ARE SIMPLY NOT SHOWN.
 * "Happy Hours Happening in ___" cannot have its tail cut off and still be a
 * sentence, and writing a replacement tail would be inventing copy — RULE 4.
 * Four of the nine name no place at all, so a screen with no location turns
 * through those four and gains the other five the moment the area arrives.
 *
 * ⚠️ SLOWER THAN THE HOT KEYS AND OFFSET FROM THEM, for the locked file's own
 * stated reason: the keys turn every 7s, so a headline on the same clock makes
 * the whole top of the screen twitch at once. Nine seconds, three seconds
 * behind. Its numbers, not new ones.
 * ══════════════════════════════════════════════════════════════════════════ */
/* ⚠️⚠️ NO PLACE IN ANY HEADLINE — Al, 1 October 2026: "Do we have to write
   Hong Kong or Calgary? Why don't we leave it generic so it doesn't really
   matter where we are… it alleviates a lot of issues and problems." His yes
   the same day to dropping ONLY the place from the five that named one; the
   other four are untouched. The pin in the top bar already says where; every
   "wrong city" fault this screen has had (Laoshan, Futian) came in through
   that slot, and now there is no slot. */
const HEADLINES = [
  'Discover the Best Food & Drinks',
  'Find the Best Deals Now',
  'Happy Hours Happening Now',
  "Today's Best Deals",
  'Last-Minute Specials Near You',
  'Game Day Deals',
  'Great Food Coming Your Way',
  'What Are You Craving?',
  'What You Crave Is Here',
];
const HEAD_TURN_MS = 9000;
const HEAD_DELAY_MS = 3000;
const HEAD_FADE_MS = 450;

/* ⚠️ THE FIRST ONE IS THE LOCKED ARRAY'S FIRST ONE WHENEVER IT CAN BE — the
   line Al signed the homepage off on. The locked file starts at random, and
   on a screen that also starts with no area that would open Eleat on
   "What Are You Craving?" as often as not. It walks in order from there, so
   every headline still gets its turn. */
/* ⚠️ THE BANNER IS SUPER ADMIN'S NOW (181, Al-Noor 10 Oct 2026: "we need to
   change that whenever we want… for each different city… add languages").
   The city's lines come from /discovery/headlines with their words in every
   language; the nine above are only the fallback, so the banner is never
   blank when the server answers nothing. */
function headlinesFor(fromServer) {
  const rows = Array.isArray(fromServer) ? fromServer.map(hotKeyLabel).filter(Boolean) : [];
  return rows.length ? rows : HEADLINES;
}

/* ⚠️ THE THREE LANGUAGES ARE THE LOCKED SCREEN'S OWN `.lang-menu` ROWS, and
 * the codes are the SCHEMA'S: `users.preferred_language text not null default
 * 'en'`, commented `-- en, zh-Hant (HK), zh-Hans, ...`. Neither the words nor
 * the codes are invented, and `PUT /settings/me` already accepts the field. */
/* ⚠️ THE CODES ARE THE `locales` TABLE'S (013) — zh-HK and zh-CN — the same
   ones Profile's language row and Zaryna use. This menu wrote zh-Hant /
   zh-Hans (001's comment), so one Eleater could hold two codes for one
   language depending on which screen they last used; both old codes are still
   read correctly everywhere (i18n.js, zaryna.language.js). 5 Oct 2026. */
const LANGUAGES = [
  { code: 'en',    native: 'English',  label: 'English' },
  { code: 'zh-HK', native: '繁體中文', label: 'Cantonese' },
  { code: 'zh-CN', native: '简体中文', label: 'Mandarin' },
  /* 173 — Al-Noor, 9 Oct 2026: "add the fourth language of French" */
  { code: 'fr-CA', native: 'Français', label: 'French (Canada)' },
  /* Gujarati is NOT here — Al-Noor, 10 Oct 2026: Zaryna answers and speaks it,
     the app is not translated into it (175) */
];
const sameLang = (a, b) => normLang(a) === normLang(b);
/* ⚠️ THE KEY LIVES IN voice.js, WHICH IS WHERE IT IS READ FROM. Home writes
   the Eleater's language choice; the microphone reads it to know what to
   listen for. Two copies of a storage key drift exactly the way two copies of
   a card do, and only one side ever notices. */


/* ⚠️ THE LAST RAILS HOME DREW, KEPT WHILE THE APP IS OPEN — Al, 2 Oct 2026:
   "why is it sluggish. did we not agree to cache it". Taps switch screens in
   place, so coming back to Home re-mounted it and drew four "Loading…" rows
   every time. Now it draws what it had at once and asks again behind it —
   the new answer replaces the old when it lands. Same city, same distance,
   or nothing is reused (`asked` below is the key). */
const lastRails = { asked: '', rails: {} };
/* ⚠️ AND KEPT ON THE PHONE BETWEEN VISITS — Al-Noor, 13 Oct 2026: opening
   Eleat drew "Loading… Loading…" for seconds (his screenshot, 10:01). The
   rails from the last visit are drawn at once — same city and distance, and
   no older than two hours, so yesterday's offers never show — and the fresh
   answer replaces them as it lands (Uber Eats', OpenRice's pattern). */
const RAILS_KEY = 'eleat.homeRails';
const RAILS_MAX_MS = 2 * 60 * 60 * 1000;
try {
  const k = JSON.parse(localStorage.getItem(RAILS_KEY) || 'null');
  if (k && k.asked && k.rails && Date.now() - k.at < RAILS_MAX_MS) { lastRails.asked = k.asked; lastRails.rails = k.rails; }
} catch { /* no saved rails — draw the shapes and ask */ }
function keepRails() {
  try {
    const ok = Object.fromEntries(Object.entries(lastRails.rails || {}).filter(([, v]) => v && v.rows));
    if (lastRails.asked && Object.keys(ok).length) localStorage.setItem(RAILS_KEY, JSON.stringify({ asked: lastRails.asked, rails: ok, at: Date.now() }));
  } catch { /* storage full or blocked — the rails still draw */ }
}

export default function Home() {
  /* each time Home opens is a visit — its rails take their next turn */
  useState(() => { rotate.newVisit('home-'); return true; });
  /* "Welcome back, {first name}" once after a sign-in, then it fades
     (Al-Noor, 31 Oct 2026: "log me into the home and say welcome back") */
  const [welcome] = useState(() => {
    try {
      const v = sessionStorage.getItem(WELCOME_KEY);
      if (v === null) return null;
      sessionStorage.removeItem(WELCOME_KEY);
      return { name: v };
    } catch { return null; }
  });
  const [welcomeGone, setWelcomeGone] = useState(false);
  useEffect(() => {
    if (!welcome) return undefined;
    const t = setTimeout(() => setWelcomeGone(true), 4000);
    return () => clearTimeout(t);
  }, [welcome]);
  /* one entry per rail: undefined = still asking, { rows, total } = answered,
     { error } = we could not ask. The three are different sentences and an
     Eleater deserves the right one. */
  const [rails, setRailsNow] = useState(() =>
    /* only if it was drawn for the city and distance Home is about to ask —
       never the last city's rails under a new city's pin, even for a frame */
    lastRails.asked === `0|${browseMarket(readCentre()).market}|${radiusLadder().join(',')}`
      ? lastRails.rails : {});
  const setRails = (next) => setRailsNow((r) => {
    const v = typeof next === 'function' ? next(r) : next;
    lastRails.rails = v;
    keepRails();
    return v;
  });
  /* ⚠️ AND A WAY TO ASK AGAIN. Changing this number re-runs the effect, which
     is the whole retry — no second fetch written out, so the two cannot
     drift. An error an Eleater can do nothing about is the dead control this
     project keeps clearing out, one layer down. */
  const [attempt, setAttempt] = useState(0);

  /* ⚠️ THE AREA IS WHEREVER THE ELEATER SAID THEY ARE — never a constant.
     RULE ZERO-B: the locked screen reads "…in Central" and "Central" is
     SAMPLE CONTENT, not a fact about Eleat. Al: *"Location pin is location
     pin."* So both the heading and the pin read the one row Location.jsx
     wrote, and if there is none the heading simply stops early rather than
     naming a place nobody chose. */
  /* ⚠️⚠️ AND IT HAS TO BE A SUBSCRIPTION, NOT A ONE-TIME READ.
     This was `useState(whereTheyAre)` — the value at the instant Home first
     rendered, and never again. `LocationAsk` finds somebody a second or two
     AFTER that, so the pin kept its old value until the page was reloaded:
     the position was correct, stored and used by every request, and the pin
     still said the wrong thing. **The very fault Al reported, one layer up
     from where it looked like it was.**
     `useCentre()` registers with the one module that owns the answer, so the
     pin, the heading, Explore and Zaryna all learn in the same instant. */
  const centre = useCentre() || {};
  /* the pin names the city (or a place they chose), never the street —
     `browsingName` in place.js, 2 Oct 2026 */
  /* outside Eleat's cities the pin names no city (16 Oct): "Set location" —
     `area` is set just below, once `fallback` is known */

  /* ⚠️ WHICH MARKET, DECIDED FROM THE CENTRE AND NOT FROM A CONSTANT.
     `null` means Eleat genuinely does not operate where they are — Shenzhen,
     Rio, San Francisco — and that is answered honestly in the headline just
     below and in the rails further down.

     ⚠️ IT IS DECLARED HERE, ABOVE ITS FIRST READER, AND THAT IS NOT TIDINESS.
     The headline needs it, and the headline is built two dozen lines from the
     top. Left where it used to sit — beside the rails, hundreds of lines down
     — this file would carry a temporal dead zone: `market` used at line 189
     and declared at 308. **That is the exact fault that killed every venue
     page for a week**, and `verify_no_tdz.js` exists because of it. */
  /* ⚠️⚠️ TWO ANSWERS, AND THEY ARE NOT THE SAME QUESTION.
     `here` — is Eleat in the city this person is standing in? That is what the
     headline and the notice below read, and it is still allowed to be null.
     `market` — what the rails should actually ASK for, so this screen is never
     blank. Al, 26 September 2026, standing in Shenzhen: *"nothing shows up. No
     restaurants, no nothing… the system looks broken."*
     ⚠️ AND `fallback` IS WHY THIS IS NOT THE THING HE FORBADE ON THE 25th.
     He said never to *silently* show Hong Kong as though it were local. It is
     not silent: when `fallback` is set the notice below names the city being
     shown and says Eleat is not in this one. Showing something and explaining
     it is the opposite of pretending. */
  const { market, fallback, unknown } = browseMarket(centre);
  const here = marketFor(centre);
  /* ⚠️ OUTSIDE ELEAT'S CITIES, THEY CHOOSE — Al-Noor, 16 Oct 2026: "if they're
     home and it says we're not in your city, period… we've got Hong Kong,
     Calgary, that's it. They click one of those." No nearest city's
     restaurants are shown for them; the line and the city buttons, nothing
     else, until they tap one (then the choice is remembered). With no
     location at all, the same buttons. */
  /* ⚠️⚠️ NEVER A BLANK HOME — Al-Noor, 30 Oct 2026 (replaces 16 Oct's "they
     choose"): "You can't just have a blank fucking screen… go back to what
     OpenTable does… the closest city." Outside Eleat's cities (or with no
     location) Home shows the NEAREST city's rails, hot keys and offers, and
     the pin names that city — OpenTable's metro pin — tap it to change. */
  const mustChoose = false;
  const [cityNames, setCityNames] = useState({});
  const area = fallback ? (fallback.name || cityNames[market] || '')
    : unknown ? (cityNames[market] || '') : browsingName(centre);

  /* the cities Eleat is in — only ever asked for when the phone gave nothing */
  const [cities, setCities] = useState([]);
  /* the hot keys: the rows themselves, which pair is showing, and the fade */
  const [hotKeys, setHotKeys] = useState([]);
  /* Home's banner lines for this city, from Super Admin (181) */
  const [banner, setBanner] = useState(null);
  const [keyAt, setKeyAt] = useState(0);
  const [keysIn, setKeysIn] = useState(true);
  const [q, setQ] = useState('');
  /* the button names the language the app is IN — chosen, else the phone's */
  const [lang, setLang] = useState(() => uiLang());
  const [langOpen, setLangOpen] = useState(false);

  /* the headline: which of the nine is showing, and whether it is mid-fade */
  const [headAt, setHeadAt] = useState(0);
  const [headIn, setHeadIn] = useState(true);
  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️⚠️ THE HEADLINE PROMISED DEALS IN A CITY ELEAT IS NOT IN.

     Al, on his own phone in Futian, 25 September 2026:
     *"the scrolling message on top, best deals in Central and all that, it
     changes to Futian, but there's nothing in Futian. So if I'm sitting in
     Brazil, is it going to say in Rio? And is it going to say best deals in
     Rio? Or if I'm in California, like in San Francisco, best deals in San
     Francisco, but there's no deals. So how do we deal with that as well?"*

     **The answer is that the area is only a slot when Eleat can fill it.**
     Five of the nine name a place — "Happy Hours Happening in ___", "Find the
     Best Deals in ___" — and every one of them is a PROMISE about that place.
     Somewhere Eleat does not operate, that promise is false before the screen
     has finished drawing, and it is false in Rio and San Francisco for the
     same reason it was false in Futian.

     ⚠️ THE FOUR THAT NAME NOWHERE ARE STILL TRUE ANYWHERE, so the screen
     still turns and nothing is invented to fill the gap — exactly the rule
     that was already written for an unknown area, now applied to the case it
     always covered and nobody had tested: a KNOWN area we do not serve.

     ⚠️ `market` IS THE TEST, NOT `area`. Knowing where somebody is and having
     something to sell them there are two different facts, and the whole of
     yesterday's work was about not confusing them.

     ⚠️⚠️⚠️ AND THAT TEST WAS STILL WRONG, AND AL CAUGHT IT ON HIS OWN PHONE IN
     LAOSHAN — 29 SEPTEMBER 2026. The headline read:

         "Discover the Best Food & Drinks in Z004, Laoshan District"

     Al: *"We're not in Laoshan… I told you either it goes to Hong Kong or it
     goes to Calgary, whichever is geographically close."*

     **The test asked the wrong question.** `market` here is `browseMarket`'s
     answer, which is ALREADY the fallback — so outside a covered city it is
     truthy (Hong Kong, because Hong Kong is nearest) while `area` is still
     the label of the street the phone is standing on. The two halves of the
     sentence came from two different places on earth. Nothing threw: a
     grammatical sentence naming the wrong city renders exactly as well as a
     right one, which is the signature of every fault in this file.

     ⚠️ THE SLOT NAMES THE CITY BEING BROWSED, NOT THE GROUND UNDERFOOT.
       · inside a market Eleat is in  → the local area — "…in Central".
         That is the locked screen's own line and it is untouched.
       · outside one, with a nearest  → that city's name — "…in Hong Kong".
         Al's instruction, in his words.
       · outside one, with no nearest → nothing. The four headlines that name
         no place still turn, and a tail is never invented (RULE 4).

     ⚠️ `fallback.name` IS THE MARKET'S OWN `name_en`, off `/discovery/where`
     via `nearestFact()` — never a slug and never a word written here. When
     the server sends only a code the name is null, and null means the slot
     stays empty rather than printing `hong-kong` at somebody. */
  const headlines = headlinesFor(banner);
  /* ⚠️ MODULO THE LIST LENGTH AT READ TIME, NEVER AT WRITE TIME. The list is
     four long with no area and nine long with one, and the area can arrive a
     second after the screen does. A counter clamped to the old length would
     have been stuck inside the first four for ever. */
  const headline = headlines[headAt % headlines.length];

  /* ⚠️ NINE SECONDS, THREE SECONDS BEHIND THE HOT KEYS, 450ms FADE, AND NOT
     WHILE THE TAB IS HIDDEN — all four are the locked file's own numbers and
     its own stated reason: a headline fading in a background tab is battery
     spent on a picture nobody is looking at. */
  useEffect(() => {
    let turn = null;
    let fade = null;
    const start = setTimeout(() => {
      turn = setInterval(() => {
        if (document.hidden) return;
        setHeadIn(false);
        fade = setTimeout(() => { setHeadAt((n) => n + 1); setHeadIn(true); }, HEAD_FADE_MS);
      }, HEAD_TURN_MS);
    }, HEAD_DELAY_MS);
    return () => {
      clearTimeout(start);
      if (turn) clearInterval(turn);
      if (fade) clearTimeout(fade);
    };
  }, []);

  /* ⚠️ THE SAME SEVEN SECONDS AND THE SAME FADE AS THE LOCKED SCREEN, which
     drops the box to opacity 0, swaps the pair after 350ms, and brings it
     back. Without the fade two words simply jump, which reads as a glitch. */
  useEffect(() => {
    const t = setInterval(() => {
      setKeysIn(false);
      setTimeout(() => { setKeyAt((n) => n + KEYS_SHOWN); setKeysIn(true); }, 350);
    }, KEYS_TURN_MS);
    return () => clearInterval(t);
  }, []);

  /* ⚠️ THE CHIPS COME OFF THE SERVER, and they come back EMPTY rather than
     falling back to anything. Al edits them in Super Admin; a copy kept here
     "just in case" would be a second list to keep in step, and on the day the
     server is slow it would put the dead ones back looking exactly like the
     live ones. `market` and not `here`, so a fallback city still gets its
     general chips — the market-scoped ones are filtered by the server. */
  /* ⚠️ ASKED FOR ONLY WHEN IT IS GOING TO BE DRAWN. A cold open that found the
     phone straight away never makes this request at all — the common case
     costs nothing, and the rare one is a small list off a route that already
     exists. */
  /* ⚠️ ALSO ASKED FOR EACH CITY'S LANGUAGES (160, Al-Noor 8 Oct 2026:
     Calgary is English only) — a small list, cached five minutes on the
     server, so it is asked on every open now rather than only when the
     city is unknown. */
  const [cityLangs, setCityLangs] = useState(() => cityLanguages());
  useEffect(() => {
    let live = true;
    call('/discovery/markets')
      .then((d) => {
        if (!live) return;
        const list = Array.isArray(d?.markets) ? d.markets : [];
        rememberCityLanguages(list);
        /* a city chosen before 10 Oct 2026 was saved without its country, so
           joining offered +852 in Calgary — filled in once, quietly */
        const c = readCentre();
        const m = c && !c.country_code && c.market && list.find((x) => x.code === c.market);
        if (m && m.country) writeCentre({ ...c, country_code: m.country });
        /* a city paused in Eleat Command (221, 30 Oct 2026) is not in this
           list — someone who had chosen it is moved to the nearest live city,
           never left on an empty Home */
        if (c && c.market && list.length && !list.some((x) => x.code === c.market)) {
          const at = Number.isFinite(c.lat) && Number.isFinite(c.lng) ? c : null;
          const closest = list.slice().sort((a, b) => (at ? metresBetween(at, { lat: Number(a.centre_lat), lng: Number(a.centre_lng) })
            - metresBetween(at, { lat: Number(b.centre_lat), lng: Number(b.centre_lng) }) : 0))[0];
          writeCentre({ ...c, market: null, nearest: { code: closest.code, name: zh(closest.name_en || closest.name, closest.name_zh) || closest.code } });
          window.location.reload();
          return;
        }
        setCityLangs(cityLanguages());
        setCities(mustChoose ? list : []);
        setCityNames(Object.fromEntries(list.map((m) => [m.code, zh(m.name_en || m.name, m.name_zh) || m.code])));
        if (languageMoved()) window.location.reload();
      })
      .catch(() => { if (live) setCities([]); });
    return () => { live = false; };
  }, [unknown]);

  /* the city being browsed decides the languages on offer; moving into a
     city that does not use the language on screen puts the screens back in
     English (and back again on returning) */
  useEffect(() => {
    if (!market) return;
    rememberBrowsingCity(market);
    setCityLangs(cityLanguages());
    if (languageMoved()) window.location.reload();
  }, [market]);

  /* an Eleater who joined before the city was kept has it filled once, from
     where the phone is (11 Oct 2026). Never asked; the server keeps it only
     when the account has none. */
  useEffect(() => {
    if (!getToken()) return;
    const city = whereCity(centre);
    if (!city) return;
    try { if (localStorage.getItem('eleat.cityKept')) return; } catch { return; }
    call('/settings/me/where', { method: 'POST', auth: true, body: { city } })
      .then(() => { try { localStorage.setItem('eleat.cityKept', '1'); } catch { /* forgets */ } })
      .catch(() => {});
  }, [centre]);

  useEffect(() => {
    if (!market) { setBanner(null); return; }
    let live = true;
    call(`/discovery/headlines?market=${encodeURIComponent(market)}`)
      .then((r) => { if (live) setBanner(Array.isArray(r?.headlines) ? r.headlines : null); })
      .catch(() => { if (live) setBanner(null); });
    return () => { live = false; };
  }, [market]);

  useEffect(() => {
    if (!market) { setHotKeys([]); return; }
    let live = true;
    call(`/discovery/hot-keys?market=${encodeURIComponent(market)}`)
      .then((r) => { if (live) setHotKeys(Array.isArray(r?.hot_keys) ? r.hot_keys : []); })
      .catch(() => { if (live) setHotKeys([]); });
    return () => { live = false; };
  }, [market]);

  /* ⚠️ THE LANGUAGE IS A REAL COLUMN, so a signed-in Eleater's choice
     survives the device. `users.preferred_language` (migration 060) and
     `PUT /settings/me` already accept it — nothing new was added for this. */
  useEffect(() => {
    if (!getToken()) return;
    let live = true;
    call('/settings/me', { auth: true })
      .then((d) => {
        const l = d && (d.preferred_language || (d.user && d.user.preferred_language));
        /* ⚠️ THE SCREEN FOLLOWS THE COLUMN, BUT THE MICROPHONE MUST NOT.
           `preferred_language` is `default 'en'`, so this arrives as 'en' for
           everyone who has never opened the menu — and storing it as a choice
           made a French phone dictate in English. `rememberLanguage()` is the
           only thing that marks a language as PICKED, and it is not called
           here on purpose. */
        /* ⚠️ A CHINESE CHOICE SAVED ON ANOTHER PHONE IS A CHOICE — 'en' is
           the column's default and cannot be told apart from one, so only a
           non-English row is taken as picked, and the app follows it. */
        /* ⚠️ SIGNED IN, THE ACCOUNT'S LANGUAGE IS THE LANGUAGE — English
           included (Al, 6 Oct 2026: "whatever language that they choose in
           their profile, they would be locked into"). adoptAccountLanguage
           keeps the microphone on the phone's language for the 'en' default. */
        if (live && l) {
          setLang(l);
          const differs = !sameLang(l, uiLang());
          adoptAccountLanguage(l);
          /* only when it stuck — a browser that refuses storage would
             otherwise reload for ever */
          if (differs && sameLang(screenLanguage(), l)) window.location.reload();
        }
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);

  function chooseLanguage(code) {
    const changed = !sameLang(code, uiLang());
    setLang(code);
    setLangOpen(false);
    /* ⚠️ THIS IS THE ONE PLACE A LANGUAGE BECOMES A CHOICE. `rememberLanguage`
       sets the flag the microphone reads; storing the key alone would put us
       back to treating the column's 'en' default as a decision. */
    rememberLanguage(code);
    /* ⚠️ SIGNED OUT IT STILL WORKS, IT JUST DOES NOT TRAVEL. Refusing the
       choice because nobody is signed in would be a dead control. */
    /* ⚠️ THE WHOLE APP CHANGES LANGUAGE, NOT ONE MENU. A reload starts every
       screen again in the new language (the translator is set once, before
       the first screen draws) — after the choice is saved, so it travels. */
    const done = getToken() ? saveMe({ preferred_language: code }).catch(() => {}) : Promise.resolve();
    if (changed) done.finally(() => window.location.reload());
  }

  /* ⚠️ THE SEARCH BELONGS TO EXPLORE — there is one search in Eleat and it is
     already built, with its four tabs, its paging and its resolver. Home hands
     the words over rather than growing a second one; a second search bar is
     how this repository ended up with three. */
  /* ⚠️ ASKED ON HOME, ANSWERED ON HOME — Al-Noor, 11 Oct 2026: "If you're
     asking from the homepage, you come back to the homepage." Words typed or
     spoken here are Zaryna's question; her steps, her answer and her cards
     appear under this bar (zask.jsx), signed in or not. Explore is one tap
     away for every match. An empty search still opens Explore. */
  const [homeAsk, setHomeAsk] = useState(() => keptQuestion('home'));
  const [homeN, setHomeN] = useState(0);
  function runSearch(text, spoken) {
    const s = String(text || '').trim();
    if (!s) { go('?explore=1'); return; }
    /* ⚠️ HER ANSWER OPENS ON HER OWN SCREEN — Al-Noor, 13 Oct 2026: "the
       answers should be in Zaryna… the centre piece", laid out one line, its
       card (Perplexity, Google AI Mode). Home stays Home; back returns here.
       (Replaces 11 Oct's answer drawn on Home under the bar.) */
    go(`?zaryna=1&from=home&q=${encodeURIComponent(s)}${spoken ? '&spoken=1' : ''}`);
  }

  /* ⚠️ A HOT KEY DOES NOT GO THROUGH `runSearch`, AND THAT IS THE WHOLE FIX.
     `runSearch` puts words in `q`, and `q` resolves a NAME, an AREA or a
     CUISINE — which is why "Happy hour in LKF" went looking for a restaurant
     of that name and found none. A chip carries real search PARAMETERS, so
     they travel as themselves. An array becomes a comma list, which the route
     splits back apart (`strArray`, fixed the same day).
     ⚠️ AND `hk=1` RIDES ALONG so Explore knows to draw ONE removable pill
     saying what was applied, rather than leaving somebody inside a filter with
     no visible way off it — the Top rated dead end, which cost a reload. */
  function runHotKey(hk) {
    const qs = new URLSearchParams({ explore: '1', hk: '1', hkname: hk.label });
    for (const [k, v] of Object.entries(hk.params || {})) {
      qs.set(k, Array.isArray(v) ? v.join(',') : String(v));
    }
    go('?' + qs.toString());
  }

  /* ⚠️ THE MICROPHONE ON HOME — SAME BAR, SAME BUTTON, SAME WIRING.
     Al, 24 September 2026: *"the search bar on the home screen should also be
     the same as the google search bar and mic."*  The words appear in the box
     as they are spoken and the search runs when the speaking stops, so this is
     still the ONE search: it ends at Explore, exactly as typing does.
     ⚠️⚠️⚠️ AND A FAILURE USED TO LEAVE **NOTHING** — WHICH IS THE WHOLE BUG.
     Al, 26 September 2026: *"the mics don't work… There was no message or exact
     words of any error. I just clicked on it. It didn't click. It didn't
     work."*  **He was describing this exactly.**

     `useSpeech` takes four callbacks and this screen passed **two**. So
     `onError` was `undefined`, and `useSpeech` does
     `cb.current.onError && cb.current.onError(e)` — so every microphone failure
     on Home was **thrown away in silence**: tap, `hearing` flickers on and off,
     no words, no message, nothing logged. **On an iPhone the most likely first
     tap is `not-allowed`**, because Safari asks for microphone permission and
     anyone who dismissed that prompt once gets a silent refusal for ever.

     ⚠️ AND THE COMMENT THAT USED TO SIT HERE IS WHY IT SURVIVED. It said *"a
     failure leaves the box alone… a red line for somebody who changed their
     mind would be Eleat telling them off for tapping."*  **That reasoning is
     correct for `no-speech` and completely wrong for a real failure**, and I
     wrote it as though it covered both — the same shape as the geocoder comment
     that justified never calling Google inside Hong Kong. A person who changed
     their mind needs no red line; a person whose microphone is blocked needs to
     be told, or the control is indistinguishable from a dead one.

     ⚠️ ZARYNA HAS HANDLED BOTH SINCE 24 SEPTEMBER — `onQuiet` into a bubble,
     `onError` into a bubble. **One hook, two screens, and only one of them was
     finished.** That is the same fault as the swallowed `no-speech` fixed on her
     side that day, still live over here.

     ⚠️ QUIET AND BROKEN ARE SAID DIFFERENTLY, deliberately: silence is a plain
     grey line, a real failure is `.err`. `voice.js` already owns the wording —
     no new copy (RULE 4). */
  const [micSaid, setMicSaid] = useState(null);   // { text, bad } | null

  const speech = useSpeech({
    onPartial: (t) => { setMicSaid(null); setQ(t); },
    onDone: (t) => { if (t) runSearch(t, true); },
    /* not a fault — somebody changed their mind, or said nothing */
    onQuiet: () => setMicSaid({ text: "I didn't catch that. Try again, or type it.", bad: false }),
    onError: (e) => setMicSaid({ text: e.message, bad: true }),
  });

  /* ══════════════════════════════════════════════════════════════════════
     ⚠️⚠️ THE RAILS NOW ASK FOR WHAT IS NEAR HIM, AND THEY NEVER DID BEFORE.
     Every rail is labelled with Al's own words — "48 spots with Happy Hour
     near you" — and the query sent no position at all, so "near you" meant
     anywhere in the market. Somebody in TST was offered Sai Kung under a line
     promising it was near them.

     Al, 24 September 2026: *"find the closest restaurants within a 500 meter
     radius and then goes out."*

     ⚠️ SO IT STARTS AT 500 m AND WIDENS UNTIL THERE IS SOMETHING TO SHOW.
     `RADIUS_LADDER` is 0.5 · 1 · 2 · 5 · the whole market. A rail stops at the
     first rung that fills, so a dense street answers at 500 m and a quiet one
     reaches further rather than showing an empty row — which would read as
     Eleat being broken instead of the street being quiet.

     ⚠️ AND IT RE-RUNS WHEN HE MOVES. `centre` comes from `useCentre()`, which
     `followMe()` updates every time the phone travels more than 100 m. Walk
     from TST to Central and these four rows are Central's. THAT is the whole
     feature; one-time is a snapshot, not a location app. */
  const near = (centre && Number.isFinite(centre.lat)) ? { lat: centre.lat, lng: centre.lng } : null;
  const nearKey = near ? `${near.lat.toFixed(4)},${near.lng.toFixed(4)}` : 'nowhere';
  const [homeAns, stopHomeAns] = useZarynaAnswer(homeAsk, { market, near, source: 'home', nonce: homeN, keep: 'home' });


  /* ⚠️ THE ELEATER'S OWN LIMIT, IF THEY SET ONE. `radiusLadder()` caps the
     rungs at `max_distance_km` and drops the whole-market rung, because
     "8 km" has to mean 8 km. Measured from `near`, which is the CENTRE —
     their own point when Current Location is active, the place they chose
     when it is not. Never the phone behind their back. */
  const ladder = radiusLadder();
  const railsFor = useRef('');

  useEffect(() => {
    let live = true;
    /* ⚠️ BACK TO Loading… ONLY FOR A NEW CITY, A NEW DISTANCE OR Try again —
       never because the phone moved. Al, 2 Oct 2026: "the sight is sluggish."
       `followMe` moves the centre every few metres (and GPS drift does it
       standing still), and each move used to blank all four rails while they
       were asked again. On a move the rails stay on screen and are replaced
       when the new answer lands. */
    const asked = `${attempt}|${market}|${ladder.join(',')}`;
    if (railsFor.current !== asked) {
      railsFor.current = asked;
      const same = lastRails.asked === asked && attempt === 0;
      lastRails.asked = asked;
      setRails(same ? lastRails.rails : {});
    }
    /* nothing to ask for where Eleat has no venues — see the notice below */
    if (!market) return () => { live = false; };

    /* ⚠️ ONE RAIL WALKS ITS OWN LADDER. Happy Hour may fill at 500 m while
       Game Day needs five kilometres — widening all four together would drag
       the dense one out to match the sparse one. */
    /* ══════════════════════════════════════════════════════════════════════
       ⚠️⚠️⚠️ ONE REQUEST PER RAIL, NOT FIVE. THIS IS THE SLUGGISHNESS.
       Al, 26 September 2026: *"it's still too fucking slow. It's really,
       really sluggish… loading, loading, loading, loading."*

       This walked the ladder — 0.5 km, 1 km, 2 km, 5 km, then the whole
       market — **waiting for each answer before asking the next**. A rail
       with nothing close by therefore made FIVE round trips before it drew
       anything, and the emptier the surroundings the slower Home got. Which
       is why it was at its very worst exactly where he was standing.

       ⚠️ THE FIX KEEPS THE LADDER'S BEHAVIOUR AND DROPS THE WAITING. Ask the
       WIDEST rung once — that answer contains every venue any tighter rung
       could have returned, with `distance_km` on each row — then pick the
       tightest rung that has enough in it, here, with no network at all.
       Same rail, same radius shown, a fifth of the waiting.

       ⚠️ AND IT IS NOT "just ask for everything". The rung is still chosen
       and still reported, because `radiusKm` is what the rail tells an
       Eleater it searched. Widening silently would be a different product.
       ══════════════════════════════════════════════════════════════════════ */
    async function fill(rail) {
      const rungs = near ? ladder : [null];
      const widest = rungs[rungs.length - 1];
      const d = await call(railQuery(market, rail.type,
        { near, radiusKm: widest, placement: true }));
      const all = d.results || [];

      /* the tightest rung that already has enough — measured, not re-asked */
      for (const km of rungs) {
        if (km === null || km === widest) break;
        const within = all.filter((r) => r && r.distance_km != null
          && Number(r.distance_km) <= km);
        if (within.length >= 4) {
          /* the count beside "View All" is how many venues View All holds
             (the server's total) — it printed the cards on this rung
             (audit, 7 Oct 2026: "View All (12)" over 33) */
          return { rows: within, total: d.total, radiusKm: km,
            placement: d.placement || null };
        }
      }
      return { rows: all, total: d.total, radiusKm: widest,
        placement: d.placement || null };
    }

    /* ⚠️ `placement: true` IS WHAT MAKES THIS THE PAID SURFACE, and it is
       asked for here and nowhere else. Al, 23 September 2026: *"the view all
       carousel is not a fucking paid. only the fucking premium placement on
       the homescreen is."*  ViewAll.jsx calls the same `railQuery` for the
       same four deal types and deliberately does not pass this. */

    for (const rail of RAILS) {
      fill(rail)
        .then((got) => live && setRails((r) => ({ ...r, [rail.type]: got })))
        /* a failed re-ask after a move keeps the rail it already drew */
        .catch((e) => live && setRails((r) => (r[rail.type] && r[rail.type].rows ? r : {
          ...r, [rail.type]: { error: e.message },
        })));
    }
    return () => { live = false; };
    /* ⚠️ THE MARKET AND THE LIMIT ARE DEPENDENCIES TOO. Walking across a
       border re-asks; so does changing the distance preference. Without them
       the rails would keep showing the city somebody has already left. */
  }, [attempt, nearKey, market, ladder.join(',')]);

  /* ⚠️ ONE BUTTON FOR ALL FOUR, BECAUSE ONE SERVER SERVES ALL FOUR. Four
     separate "try again" links would have an Eleater tapping four times to
     recover from a single thing not answering. */
  const allFailed = RAILS.every((r) => rails[r.type] && rails[r.type].error);

  /* ⚠️ THE AREA IS NOT SHOWN UNTIL ELEAT KNOWS IT — 22 September 2026.
     The locked hero reads "Discover the Best Food & Drinks in Central".
     Central is a place a device was told about, not a constant, and writing
     one in would be inventing where somebody is standing.

     ⚠️ AND MY FIRST ATTEMPT CALLED THE WRONG ROUTE, WRONGLY. I reached for
     GET /discovery/my-place and read `name_en`. That route requires a signed-in
     Eleater — Home is the first screen anyone sees, signed out — and it returns
     `{ place: { label, address, lat, lng } }`, a saved ADDRESS, not the name of
     an area. It would have 401'd for every new arrival and read a field that
     does not exist for everyone else.

     So the line names no area until there is one to name. When location and
     the area resolver are wired, the area arrives here and the sentence
     completes itself. An unfinished sentence is better than a wrong place. */

  const shown = RAILS.filter((r) => {
    const s = rails[r.type];
    return !s || s.error || (s.rows && s.rows.length > 0);
  });

  return (
    <div className="home">
      {/* ══════════════════════════════════════════════════════════════════
          HOME'S HEAD, IN THE LOCKED SCREEN'S OWN ORDER
          topbar · hero · search · hot keys · pillars
          ══════════════════════════════════════════════════════════════════ */}
      <header className="hhead">
        <div className="htop">
          <div className="logo">
            <img className="lmark" src="/eleat_mark.png" alt="" />
            <img className="lword" src="/eleat_word.png" alt="Eleat" />
          </div>

          {/* ⚠️⚠️ THE PIN LIVES UP HERE NOW — 24 September 2026. Al, on his own
              phone: *"the location, search bar and mic look too cramped you can
              put location on the top bar and expand the search bar and mic."*
              It MOVED; it did not change. Same label, same `?location=1`, same
              "Set location" until Eleat knows the area — RULE ZERO-B, and Al's
              own *"the location pin is a pin"*. There is room because the
              locked screen's late cascade switches the Profile icon off here:
              `.topbar .icbtn[title="Profile"]{display:none!important}`. */}
          {/* ⚠️ A SPACER EACH SIDE — 25 September 2026. There was one, after
              the pin, so every spare pixel went to the right of the bar and
              the pin ended up leaning on the wordmark. Al: *"look how close
              it is to my Eleat logo."*  Two splits the slack evenly. */}
          <div className="spacer" />

          <LocationPin label={area || 'Set location'} href="?location=1" />

          <div className="spacer" />

          {/* one language in this city (Calgary: English, 160) → no menu */}
          {!(cityLangs && cityLangs.length <= 1) && (
          <div className="lang-wrap">
            <button type="button" className="lang-btn"
              aria-expanded={langOpen} aria-haspopup="true"
              onClick={() => setLangOpen((o) => !o)}>
              {/* the globe says "languages" (Uber's language button, Al-Noor yes,
                  1 Nov 2026) — names only in the menu, never flags */}
              <svg className="lglobe" viewBox="0 0 24 24" width="14" height="14" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
              </svg>
              {(LANGUAGES.find((l) => sameLang(l.code, lang)) || LANGUAGES[0]).code === 'en'
                ? 'EN ▾'
                /* "Français ▾" ran onto two lines in the top bar — FR, like EN */
                : (LANGUAGES.find((l) => sameLang(l.code, lang)) || LANGUAGES[0]).code === 'fr-CA'
                  ? 'FR ▾'
                  : (LANGUAGES.find((l) => sameLang(l.code, lang)) || LANGUAGES[0]).code === 'gu-IN'
                  ? 'GU ▾'
                  : (LANGUAGES.find((l) => sameLang(l.code, lang)) || LANGUAGES[0]).native + ' ▾'}
            </button>
            {langOpen && (
              <div className="lang-menu" role="menu">
                {/* only the languages this city uses (160) — Calgary: English · Français */}
                {LANGUAGES.filter((l) => !cityLangs || cityLangs.some((c) => sameLang(c, l.code))).map((l) => (
                  <button key={l.code} type="button" role="menuitem"
                    className={sameLang(l.code, lang) ? 'lang-row active' : 'lang-row'}
                    onClick={() => chooseLanguage(l.code)}>
                    <span className="chk">{sameLang(l.code, lang) ? '✓' : ''}</span>
                    <span className="lnative">{l.native}</span>
                    <span className="len">{l.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          {/* ⚠️ NO PROFILE ICON, AND THAT IS THE LOCKED SCREEN'S OWN DECISION.
              `10_Home_LOCKED.html` ends with
              `.topbar .icbtn[title="Profile"]{display:none!important}` — the
              icon is switched off there and Profile is reached from the tab
              bar. Drawing one here would be adding back a control Al removed. */}
          <a className="icbtn" href="?notifications=1" title="Notifications"
            aria-label="Notifications">
            <svg viewBox="0 0 24 24" width="19" height="19" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              strokeLinejoin="round">
              <path d="M18 8a6 6 0 10-12 0c0 7-3 8-3 8h18s-3-1-3-8" />
              <path d="M13.7 21a2 2 0 01-3.4 0" />
            </svg>
            {/* ⚠️ NO NUMBER UNTIL THERE IS A NUMBER. The locked screen's badge
                starts hidden and is filled in by script; nothing on the server
                counts unread notices yet, and a badge showing 0 — or showing
                anything at all — would be an invented count. */}
          </a>
        </div>

        {/* ⛔ Al-Noor, 31 Oct 2026, on his phone in Shenzhen: "Why are we still
            telling ppl where they are… not what we talked about." The
            "Browsing Hong Kong — you're about 31 km away · Use where I am" box is
            gone. The pin names the city and a tap on it changes it — OpenTable's
            shape, his 30 Oct rule (never-blank Home). Never bring it back. */}

        <div className="hhero">
          {welcome && (
            <p className={'welcomeback' + (welcomeGone ? ' gone' : '')} role="status">
              {'Welcome back'}{welcome.name ? <span>, {welcome.name}</span> : null}
            </p>
          )}
          {/* ⚠️ ALL NINE, TURNING. This drew the first one and sat on it —
              the exact fault Al reported on the prototype on 9 September and
              which came back when the React screen was written. */}
          <h1 className="heroline"
            style={{ opacity: headIn ? 1 : 0, transition: `opacity ${HEAD_FADE_MS}ms ease` }}>
            {headline}
          </h1>
        </div>

        {/* ══════════════════════════════════════════════════════════════════
            ⚠️⚠️ THE GOOGLE SHAPE — MAGNIFIER LEFT, WORDS MIDDLE, MICROPHONE
            RIGHT. Al, 24 September 2026, and this is a DELIBERATE DEPARTURE
            FROM `10_Home_LOCKED.html`, which puts an orange circular
            `.btn-orange.btn-search` at the RIGHT and no microphone anywhere.
            His explicit current instruction is precedence #1, above the locked
            screen, and it is written here so nobody later "restores the locked
            screen" and puts the orange button back.

            ⚠️⚠️ AND THE PIN IS NOT ON THIS BAR ANY MORE — 24 September 2026.
            Al: *"the location, search bar and mic look too cramped you can put
            location on the top bar and expand the search bar and mic."*  Three
            controls were fighting for one row on a phone: the placeholder was
            cut off mid-word and the microphone was pushed off the end. The pin
            is `LocationPin` in `.htop` above — the same control, still a pin,
            still opening `?location=1`. What is left here is exactly what he
            asked for: the magnifier, the words, and the microphone with room.

            ⚠️ AND IT IS STILL NOT A SECOND SEARCH. `onSubmit` hands the words
            to Explore as `?explore=1&q=…`, typed or spoken, exactly as before.
            The bar is shared with Zaryna; the destination is not. */}
        <SearchBar className="hsearch"
          value={q} onChange={setQ} onSubmit={() => runSearch(q)}
          placeholder="Search Eleat…"
          goLabel="Search"
          mic={speech.allowed
            ? { on: speech.hearing, onTap: speech.tap, label: 'Search by voice' }
            : null} />

        {/* ⚠️ DIRECTLY UNDER THE BAR THAT OWNS THE MICROPHONE, so the message
            is beside the control that produced it rather than somewhere else on
            a long screen. `role="status"` so a screen reader announces it
            without stealing focus mid-search.
            ⚠️ AND IT CLEARS ITSELF the moment words start arriving (`onPartial`
            above), so a failed tap followed by a good one does not leave a stale
            red line under a search that worked. */}
        <MicStatus speech={speech} />
        {micSaid && (
          <p className={micSaid.bad ? 'err micsaid' : 'mut micsaid'} role="status">
            {micSaid.text}
          </p>
        )}
        <ZarynaAnswer question={homeAsk} ans={homeAns} withCards
          onStop={() => { stopHomeAns(); setHomeAsk(''); }} />
        {homeAns && homeAns.state === 'done' && (
          <a className="link zmore" href={`?explore=1&q=${encodeURIComponent(homeAsk)}&asked=1`}>
            See every match in Explore ›
          </a>
        )}

        {/* ⚠️ THE HOT KEYS — two at a time, cycling, off the server. Drawn only
            when there are any: an empty `.hkeys` is a padded empty row under
            the search bar, and a chip with no search is the dead control this
            whole change exists to remove. */}
        {/* hot keys and the offer buttons belong to a city — hidden until one is chosen */}
        {!mustChoose && hotKeys.length > 0 && (
          <div className="hkeys" style={{ opacity: keysIn ? 1 : 0 }}>
            {Array.from({ length: Math.min(KEYS_SHOWN, hotKeys.length) },
              (_, k) => hotKeys[(keyAt + k) % hotKeys.length])
              .map((hk) => (
                <button key={hk.label} type="button" className="chip"
                  onClick={() => runHotKey(hk)}>{hotKeyLabel(hk)}</button>
              ))}
          </div>
        )}

        {/* the four offer buttons — `.pillars`, NOT the hot keys. Each opens
            that offer's View All, exactly as the locked screen's do. */}
        {!mustChoose && (
          <div className="pillars">
            {RAILS.map((r) => (
              <a key={r.type} className="pillar" href={`?all=${r.type}`}>{r.title}</a>
            ))}
          </div>
        )}
      </header>

      <main>
        {/* ══════════════════════════════════════════════════════════════════
            ⚠️⚠️ ELEAT IS NOT EVERYWHERE, AND IT SAYS SO INSTEAD OF SERVING
            HONG KONG.

            Al, 25 September 2026: *"If I am physically in Singapore, Shenzhen,
            Vancouver, Beijing or anywhere else where Eleat currently has no
            restaurant market… do NOT silently show Hong Kong restaurants as
            though they were local. I must still be able to deliberately
            choose Hong Kong, Calgary or another supported Eleat market."*

            The Location screen has said this correctly all along — *"Eleat is
            not in this area yet"*, the same words, which is why they are the
            words here (RULE 4). Home was the screen that carried on asking for
            Hong Kong regardless, so the pin read "Marina Bay" above four rails
            of Wan Chai happy hours and nothing anywhere explained it.

            ⚠️ AND IT IS A DOOR, NOT A DEAD END. The pin above goes straight to
            the location screen, where the cities Eleat IS in are one tap.
            ══════════════════════════════════════════════════════════════════ */}
        {/* ⚠️ THE SAME WORDS THE LOCATION SCREEN USES — *"Eleat is not in this
            area yet"* — with the city being shown instead named beside them,
            so nothing is pretended and nothing is blank. RULE 4: the sentence
            is Eleat's own; only the city name is filled in. */}
        {/* ⛔ REMOVED — Al, 5 Oct 2026, on his phone in mainland China: "Why is
            the notification Eleat is not in this area… keep on coming up? I
            thought we got rid of it." Since 2 Oct the pin at the top names the
            city being shown (OpenTable's way), so this line said it twice. The
            pin is still the way to choose another place. */}
        {/* ⚠️ ONLY WHEN THERE IS AN AREA TO BE "NOT IN" — 30 September 2026.
            `centre` above is `useCentre() || {}`, so with no location at all
            `marketFor({})` is null and this sentence told somebody whose
            phone had given Eleat nothing that Eleat was not where they were —
            a claim about a place nobody knew. That case is `unknown`, and the
            CITIES ELEAT IS IN row just below is the only thing it draws. */}

        {/* ══════════════════════════════════════════════════════════════
            ⚠️⚠️ THE ONLY TIME ANYBODY IS ASKED WHERE THEY ARE.

            Al, 29 September 2026, on the full-screen sheet that used to open
            before the phone's own prompt: *"I've asked you so many fucking
            times to remove it… It simply says, use your fucking location and
            that's it."*  That sheet is gone and is not coming back.

            ⚠️ THIS IS NOT THAT SHEET. It appears only when the phone has given
            Eleat nothing at all — refused, or no signal — which is the one
            case where the app genuinely cannot know and was, until today,
            quietly showing Hong Kong to somebody in Toronto with nothing
            saying so. It is a row under the search bar, not a screen in front
            of one, and it takes one tap.

            ⚠️ AND IT IS TWO CITIES BECAUSE ELEAT IS IN TWO CITIES. The list is
            `/discovery/markets`, so it is however many Eleat actually operates
            in — Hong Kong and Calgary today, both live with real venues,
            checked on the server rather than assumed. Nothing here is a
            hard-coded city name.

            ⚠️ EVERY WORD AND EVERY CLASS IS THE LOCATION SCREEN'S OWN —
            `08j_Location.html`'s "CITIES ELEAT IS IN", its `.sect` heading and
            its `.chip` buttons — and the tap does exactly what that screen's
            chips do, through the same `centreFrom(…, {from:'chosen'})`. A
            second way of choosing a city would be a second answer to "where am
            I browsing", and only one of them would get fixed next time.
            ══════════════════════════════════════════════════════════════ */}
        {mustChoose && cities.length > 0 && (
          <div className="loc">
            {/* Al-Noor's words, 16 Oct 2026 — no "sorry", no city named (a farm,
                a VPN or a poor signal would name the wrong one) */}
            {fallback && <p className="mut">{"Eleat isn't in your location yet."}</p>}
            <div className="sect">CITIES ELEAT IS IN</div>
            <div className="chipbox">
              {cities.map((m) => {
                const name = zh(m.name_en || m.name, m.name_zh) || m.code;
                return (
                  <button key={m.code} type="button" className="chip"
                    onClick={() => {
                      const next = centreFrom({
                        label: name,
                        market: m.code,
                        lat: Number(m.centre_lat),
                        lng: Number(m.centre_lng),
                        /* the city's country — joining's phone code and billing
                           country read it (+852 was offered in Calgary) */
                        country_code: m.country,
                      }, { from: 'chosen' });
                      writeCentre(next);
                      /* ⚠️ THE SAME RELOAD THE LOCATION SCREEN'S CHIPS DO.
                         Home reads the centre once at the top; setting it
                         without turning the page leaves every rail asking the
                         old market, which is the half-built chip Al caught on
                         29 September — the colour changed and nothing else. */
                      location.href = '?';
                    }}>{name}</button>
                );
              })}
            </div>
          </div>
        )}
        {allFailed && (
          <p className="mut">
            <button type="button" className="link"
              onClick={() => setAttempt((n) => n + 1)}>Try again</button>
          </p>
        )}
        {!mustChoose && shown.map((rail) => (
          <Rail key={rail.type} rail={rail} state={rails[rail.type]} />
        ))}
        {/* ⚠️ EVERY RAIL ANSWERED AND EVERY ONE WAS EMPTY. Not an error, and
            not a blank screen either — it is what a market looks like before
            anybody has published an offer, and it says so. */}
        {market !== null && !mustChoose && shown.length === 0 && (
          <p className="mut">No offers published in this market yet.</p>
        )}
        {/* no footer in the app — Al-Noor, 14 Oct 2026: apps carry none; Terms,
            Privacy and Cookies are in Settings, the email in Help */}
      </main>

      <TabBar active="home" />
    </div>
  );
}

/* (The app's footer was removed — Al-Noor, 14 Oct 2026. Terms, Privacy and
   Cookies open inside the app from Settings (Legal.jsx); the email is in Help.) */

/* ⚠️ PREMIUM PLACEMENT PER AREA (186, Al-Noor 11 Oct 2026): the restaurants
   holding a place in the Eleater's own area come first and take turns among
   themselves; everybody else follows in the rail's usual order. A row is
   `paid` only when the server says so (and only once selling is switched on),
   so with nobody paid this is exactly the rail as it always was. */
function railOrder(rail, rows) {
  const paid = rows.filter((v) => v.paid);
  const rest = rows.filter((v) => !v.paid);
  return [...rotate.rail(`home-paid-${rail.type}`, paid),
          ...onNowFirst(rotate.rail(`home-${rail.type}`, rest), rail.type)];
}

function Rail({ rail, state }) {
  /* what this rail showed, in the order shown — Analytics' carousel page */
  const order = state && state.rows ? railOrder(rail, state.rows).map((v) => v.id).join(',') : '';
  useEffect(() => { if (order) shown(rail.type, order.split(',')); }, [rail.type, order]);
  return (
    <section className="section" id={`rail-${rail.type}`}>
      <div className="section-head">
        <h2 className="section-title">{rail.title}</h2>
        {/* ⚠️ THE COUNT IS THE SERVER'S TOTAL, NEVER rows.length. The rail
            holds twelve; the total may be four thousand. Printing the number
            of cards on screen as though it were the number of venues is the
            "an event count is not a person count" fault in another costume. */}
        {state && typeof state.total === 'number' && (
          <a className="viewall" href={`?all=${rail.type}`}>
            View All ({countLabel(state.total)})
          </a>
        )}
      </div>

      {/* grey card shapes while it asks, never the word "Loading…" (13 Oct) */}
      {!state && (
        <div className="rskel" aria-label="Loading" role="status">
          <span className="rskel-c" /><span className="rskel-c" />
        </div>
      )}
      {state && state.error && <p className="err">{state.error}</p>}
      {state && state.rows && (
        /* ⚠️ THE ORDER IS DECIDED BEFORE THE ROW IS DRAWN, AND IT ADVANCES ON
           EVERY PAGE LOAD. `rotate.rail()` is `eleat_rotate.js`'s `turnFor` —
           Al: *"All the other ones are turning and turning and turning."*
           Home is a shop window nobody is trying to find their way back to.
           ⚠️ KEYED BY THE DEAL TYPE, never by anything the rotation changes.
           The prototype once keyed a rail on the first card's name, so after
           one turn the counter was written under one key and looked for under
           another and slot one reset for ever. */
        <Carousel
          endcard={state.total > state.rows.length ? (
            <a className="card endcard" href={`?all=${rail.type}`}>
              View all {countLabel(state.total)}
            </a>
          ) : null}
        >
          {weave(railOrder(rail, state.rows), state.placement, rail)}
        </Carousel>
      )}
    </section>
  );
}


/* ══════════════════════════════════════════════════════════════════════════
 * THE VENUE CARDS, WITH THE BRAND'S CARD AMONG THEM
 *
 * ⚠️ THE ROTATION HAPPENS FIRST, AND THE BRAND CARD IS PLACED AFTERWARDS.
 * `rotate.rail()` turns the venues by one on every page load so every venue
 * reaches slot one — Al's rule, and `eleat_rotate.js` has held it since
 * September. If the brand card were woven in before the turn it would ride
 * round with them and appear in a different place every load, which is not
 * "every 10th restaurant"; and worse, it would take a venue's turn at slot
 * one. **A brand does not stand in the rotation restaurants pay to be in.**
 *
 * ⚠️ AND THE ORDER INSIDE THIS FUNCTION IS WHY IT IS A FUNCTION. Doing it
 * inline in the JSX is how it would quietly end up on the wrong side of the
 * turn the next time somebody edits this row.
 * ══════════════════════════════════════════════════════════════════════════ */
function weave(rows, placement, rail) {
  const cards = rows.map((v, i) => (
    <VenueCard key={v.id} v={v} tab={rail.tab} railType={rail.type}
      placement={placement} slot={i + 1} />
  ));
  /* ⚠️ THE SLOTS COME FROM THE SETTINGS, THROUGH placement.js. Home does not
     know the interval, the minimum row length or the maximum per row, and
     must not: they are rows in `platform_settings` that the Super Admin
     changes. An empty answer is the ordinary case — there is no campaign
     running in any city today. */
  const slots = slotsFor(cards.length, placement);
  if (!slots.length) return cards;

  const out = [];
  let next = 0;
  for (let i = 0; i <= cards.length; i++) {
    while (next < slots.length && slots[next] === i) {
      out.push(<BrandCard key={`brand-${rail.type}-${i}`} card={placement.brand_card}
        placement={placement} railType={rail.type} />);
      next += 1;
    }
    if (i < cards.length) out.push(cards[i]);
  }
  return out;
}


/* ⚠️ THE CARD AND THE TAB BAR LIVE IN ./ui.jsx. They were declared here while
   Home was the only screen that drew them; Explore, the five View All pages
   and Saved all need the same two. Two copies of a card is how one screen
   quietly starts showing a rating the other hides. */
